import {
  type PayloadAction,
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';

import { envVars } from '../../envVars';
import type { ExtrajudicialNotification, Paginated } from '../../types';
import { debouncePromise } from '../../utils/helpers';
import { headers } from '../../utils/requests/base-headers';
import { type BaseFetchProps, defaultBaseFetchProps, fetchGet } from '../utils';
import type { EjnFilter } from './ejn-filters';

type EjnList = BaseFetchProps & {
  initialLoadComplete: boolean;
  extrajudicialNotifications: ExtrajudicialNotification[];
  numberOfElements: number;
  totalPages: number;
};

const initialState: EjnList = {
  ...defaultBaseFetchProps,
  initialLoadComplete: false,
  extrajudicialNotifications: [],
  numberOfElements: 0,
  totalPages: 0,
};

const debouncedFetch = debouncePromise(
  fetchGet<Paginated<ExtrajudicialNotification>>,
  300,
);

export const fetchExtrajudicialNotifications = createAsyncThunk<
  Paginated<ExtrajudicialNotification>,
  EjnFilter,
  { rejectValue: string }
>(
  'extrajudicialNotifications/getExtrajudicialNotifications',
  async (
    {
      pageNumber,
      pageSize,
      statuses,
      sentAt,
      repossessionStartedDate,
      notificationStatuses,
    },
    thunkApi,
  ) => {
    const buildQueries = (): string => {
      const filter =
        `${pageNumber ? `&page=${pageNumber}` : ''}` +
        `${pageSize ? `&size=${pageSize}` : ''}` +
        `${sentAt ? `&sentAt=${sentAt}` : ''}` +
        `${
          statuses?.length
            ? `&status=${statuses.map((it) => `${it}`).join(',')}`
            : ''
        }` +
        `${
          notificationStatuses.length
            ? `&notificationStatus=${notificationStatuses
                .map((it) => `${it}`)
                .join(',')}`
            : ''
        }` +
        `${
          repossessionStartedDate
            ? `&repossessionStartedDate=${repossessionStartedDate}`
            : ''
        }`;

      return filter ? `${filter.replace(/^&/, '?')}` : filter;
    };

    const url = `${
      envVars.CREDITAS_HOST
    }/extrajudicial-notifications${buildQueries()}`;

    try {
      const response = await debouncedFetch(url, headers);

      return response;
    } catch (_error) {
      return thunkApi.rejectWithValue(
        'Failed to fetch extrajudicial notifications.',
      );
    }
  },
);

export const extrajudicialNotificationsSlice = createSlice({
  name: 'extrajudicialNotifications',
  initialState,
  reducers: {
    reset: () => initialState,
    updateEjnFromList: (
      state,
      action: PayloadAction<ExtrajudicialNotification>,
    ) => {
      const index = state.extrajudicialNotifications.findIndex(
        (n) => n.id === action.payload.id,
      );
      if (index !== -1) {
        state.extrajudicialNotifications[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExtrajudicialNotifications.pending, (state) => {
        state.loading = true;
        state.success = undefined;
        state.error = null;
        state.numberOfElements = 0;
        state.totalPages = 0;
      })
      .addCase(fetchExtrajudicialNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.extrajudicialNotifications = action.payload?.data;
        state.numberOfElements = action.payload?.numberOfElements;
        state.totalPages = action.payload?.totalPages;
        state.initialLoadComplete = true;
      })
      .addCase(fetchExtrajudicialNotifications.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.error.message || 'Something went wrong';
        state.numberOfElements = 0;
        state.totalPages = 0;
        state.initialLoadComplete = true;
      });
  },
});

export const { reset, updateEjnFromList } =
  extrajudicialNotificationsSlice.actions;

export default extrajudicialNotificationsSlice.reducer;
